import { IssueFilterQuery } from 'components/issues/IssueTracker.types';

import type { IssueFilters } from '../issues.reducer';

/**
 * Every key that is a filter, and nothing else.
 *
 * `IssueFilterQuery` is already the list; this is it at runtime, so the URL parser, the redux
 * boundary and the persistence boundary all work from one definition instead of three that drift.
 */
export const ISSUE_FILTER_FIELDS = Object.values(IssueFilterQuery);

/**
 * Compile-time guard: these only resolve while `IssueFilterQuery` and `IssueFilters` name exactly
 * the same keys, so adding a filter to one without the other fails the build.
 */
type EveryQueryIsAFilter<T extends keyof IssueFilters> = T;
type EveryFilterIsAQuery<T extends IssueFilterQuery> = T;
export type IssueFilterQueryMatchesIssueFilters = [
	EveryQueryIsAFilter<IssueFilterQuery>,
	EveryFilterIsAQuery<keyof IssueFilters>,
];

/**
 * Keeps only the filter keys, dropping everything else the source carries.
 *
 * The issue page keeps state on the same URL as its filters, and `fetchInitialIssue` used to
 * dispatch the entire query string into the filter slice minus four names it knew about. A saved
 * filter is `tempFilters` verbatim, so whatever else was on the URL was persisted, then replayed
 * onto the URL the next time the filter was applied - a date range or a search the user never
 * chose and could not see (Fizzy #2034, #2044).
 *
 * This replaces the earlier `stripPersistedDateRange`, which named `qStartDate`/`qEndDate` by
 * hand. A whitelist covers those two and every key nobody has thought of yet, and it neutralises
 * filters saved before the fix without a migration.
 */
export function pickIssueFilters(source?: Record<string, unknown> | null): Partial<IssueFilters> {
	if (!source) return {};

	const filters: Record<string, unknown> = {};
	for (const field of ISSUE_FILTER_FIELDS) {
		if (source[field] !== undefined) filters[field] = source[field];
	}

	return filters as Partial<IssueFilters>;
}
